import React from "react";
import { useForm, FormProvider, FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardHeader, CardBody, CardFooter, Button, Divider, addToast } from "@heroui/react";
import { Icon } from "@iconify/react";
import { MedicalQuestionsForm } from "../../admin/AdminCreateRequest/MedicalQuestionsForm";
import { DosageForm } from "../../admin/AdminCreateRequest/DosageForm";
import { DocumentsUploadForm } from "../../admin/AdminCreateRequest/DocumentsUploadForm";
import { TransportersForm } from "../../admin/AdminCreateRequest/TransportersForm";
import { SubmissionDeclaration } from "../SubmissionDeclaration/SubmissionDeclaration";
import { useSubmissionDeclaration } from "../SubmissionDeclaration/useSubmissionDeclaration";
import { RequestSchema, TRequest } from "@shared/entities";
import { useAppApi } from "src/appApi";
import { useStore } from "src/store/store";
import { usePrefillClientDetails } from "src/hooks/usePrefillClientDetails";
import { scrollToFirstError } from "src/components/forms/scrollToFirstError";
import { PersonalInfoForm } from "../../admin/AdminCreateRequest/PersonalInfoForm";

type Step = {
	fields: Array<FieldPath<TRequest>>;
};
const steps: Array<Step> = [
	// Every field step 1 shows (PersonalInfoForm renders the indication too) —
	// a field left out here is only caught by the final check, after step 5.
	{ fields: ["clientDetails", "requestType", "reasonType"] },
	{
		fields: ["medicalQuestionnaire"],
	},
	{
		fields: ["dosages", "existsDosage"],
	},
	{
		fields: ["documents"],
	},
	{
		fields: ["transporters"],
	},
] as const;

export default function ClientCreateRecommendationOnly() {
	const store = useStore();

	const methods = useForm<TRequest>({
		resolver: zodResolver(RequestSchema),
		shouldUnregister: false,
		defaultValues: {
			id: crypto.randomUUID(),
			createdAt: Date.now(),
			status: "pending",
			userId: store.user?.uid || "",
			createdBy: "client",
			isRecommendationOnly: true, // Hardcoded to true for this component
			clientDetails: {
				firstName: store.profile?.firstName || "",
				lastName: store.profile?.lastName || "",
				email: store.profile?.email || "",
				phone: store.profile?.phone || "",
				phone2: store.profile?.phone2 || "",
				IDNumber: store.profile?.IDNumber || "",
				dob: store.profile?.dob || "",
				sex: store.profile?.sex || undefined,
				healthInsurance: store.profile?.healthInsurance || undefined,
				city: store.profile?.city || "",
				street: store.profile?.street || "",
				houseNumber: store.profile?.houseNumber || "",
				apartmentNumber: store.profile?.apartmentNumber || "",
			} as any,
			documents: {},
			medicalQuestionnaire: {
				psychiatricTreatment: undefined,
				addictionHistory: undefined,
				highBloodSugar: undefined,
				highBloodPressure: undefined,
				hasHeartIssues: undefined,
				hasWeaponLicense: undefined,
				hasTransporters: undefined,
			},
			existsDosage: [],
			dosages: [],
			requestType: undefined,
			userComment: "",
		},
	});

	usePrefillClientDetails(methods);
	const declaration = useSubmissionDeclaration();

	const appApi = useAppApi();

	const { handleSubmit } = methods;
	const [isSending, setIsSending] = React.useState(false);

	const [currentStep, setCurrentStep] = React.useState(1);
	const totalSteps = steps.length;
	const isLastStep = currentStep >= totalSteps;

	const nextStep = async () => {
		if (isSending) return;
		if (isLastStep) {
			setIsSending(true);
			try {
				await onSubmit(methods.getValues());
			} finally {
				setIsSending(false);
			}
			return;
		}
		const stepDetails = steps[currentStep - 1];

		if (!stepDetails) {
			return;
		}

		const isValid = await methods.trigger(stepDetails.fields, { shouldFocus: true });
		if (!isValid) {
			appApi.api.system.uiLogs({
				message: "step validation failed",
				severity: "ERROR",
				errors: methods.formState.errors,
			});
			addToast({
				title: "שגיאת אימות",
				description: "יש שדות שלא מולאו כראוי",
				color: "danger",
				variant: "solid",
			});
			scrollToFirstError();
			return;
		}

		setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
	};

	const onSubmit = async (data: TRequest) => {
		console.log("Form submitted:", data);

		const validation = RequestSchema.safeParse(data);
		console.log("validation", validation);
		if (!validation.success) {
			appApi.api.system.uiLogs({
				message: "form error",
				severity: "ERROR",
				errors: validation.error,
			});
			addToast({
				title: "לא ניתן לשלוח את הבקשה",
				description: "חסרים פרטים בבקשה. חזור/י לשלבים הקודמים ובדוק/י שכל השדות מולאו.",
				color: "danger",
			});
			return;
		}

		// The patient declaration: required, and recorded by the server first.
		const accepted = await declaration.confirm(data.id);
		if (!accepted) return;

		try {
			// Submit recommendation-only request
			await appApi.api.client.setRequest(data);

			if (store.profile) {
				// fill client details with profile data
				await appApi.api.client.updateProfile({
					...store.profile,
					...data.clientDetails,
				});
			}

			// No payment here: an admin sends a payment link when it is due.
			store.storeApi.setActiveRequests([...store.activeRequests, data]);
			appApi.navigate("/my_profile");
		} catch (error) {
			console.error("Error submitting form:", error);
			addToast({ title: "שליחת הבקשה נכשלה", description: "נסה/י שוב בעוד רגע.", color: "danger" });
		}
	};

	const prevStep = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return <PersonalInfoForm />; // Uses local PersonalInfoForm without email
			case 2:
				return <MedicalQuestionsForm />;
			case 3:
				return <DosageForm />;
			case 4:
				return <DocumentsUploadForm />;
			case 5:
				return (
					<div className="space-y-6">
						<TransportersForm />
						<Divider />
						<SubmissionDeclaration
							accepted={declaration.accepted}
							onAcceptedChange={declaration.onAcceptedChange}
							renewalContactConsent={declaration.renewalContactConsent}
							onRenewalContactConsentChange={declaration.setRenewalContactConsent}
							showError={declaration.showError}
							errorAttempt={declaration.errorAttempt}
						/>
					</div>
				);
			default:
				return <PersonalInfoForm />;
		}
	};

	const getStepTitle = () => {
		switch (currentStep) {
			case 1:
				return "פרטים אישיים";
			case 2:
				return "שאלון רפואי";
			case 3:
				return "מינון מבוקש";
			case 4:
				return "העלאת מסמכים";
			case 5:
				return "פרטי משנעים";
			default:
				return "";
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 p-4 md:p-6">
			<div className="max-w-4xl mx-auto">
				<Card className="mb-6">
					<CardHeader className="flex flex-col gap-1">
						<h1 className="text-2xl font-bold">בקשה להמלצה בלבד</h1>
						<p className="text-default-500">מילוי פרטי בקשה להמלצה על קנאביס רפואי</p>
					</CardHeader>
				</Card>

				<div className="mb-6">
					<div className="flex justify-between items-center mb-2">
						<h2 className="text-lg font-semibold">{getStepTitle()}</h2>
						<div className="text-sm text-default-500">
							שלב {currentStep} מתוך {totalSteps}
						</div>
					</div>
					<div className="w-full bg-default-100 rounded-full h-2.5">
						<div
							className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
							style={{ width: `${(currentStep / totalSteps) * 100}%` }}
						></div>
					</div>
				</div>

				<FormProvider {...methods}>
					<form
						onSubmit={handleSubmit(onSubmit, (errors) => {
							appApi.api.system.uiLogs({
								message: "form error",
								severity: "ERROR",
								errors,
							});
						})}
					>
						<Card className="mb-6">
							<CardBody>{renderStepContent()}</CardBody>
							<Divider />
							<CardFooter className="flex justify-between">
								<Button
									variant="flat"
									color="default"
									type="button"
									startContent={<Icon icon="lucide:arrow-right" className="h-4 w-4" />}
									onPress={prevStep}
									isDisabled={currentStep === 1}
								>
									הקודם
								</Button>

								<Button
									color={isLastStep ? "success" : "primary"}
									type={"button"}
									isLoading={isSending}
									onPress={nextStep}
									endContent={
										isLastStep ? (
											<Icon icon="lucide:check" className="h-4 w-4" />
										) : (
											<Icon icon="lucide:arrow-left" className="h-4 w-4" />
										)
									}
								>
									{isLastStep ? "שלח בקשת המלצה" : "המשך"}
								</Button>
							</CardFooter>
						</Card>
					</form>
				</FormProvider>
			</div>
		</div>
	);
}
